/**
 * @fileoverview Custom OpenLayers control for switching between base tile layers.
 * @module map/TileSetSwitcherControl
 */

import { DropdownControl } from './DropdownControl.js';

/**
 * Custom OpenLayers control that provides a dropdown menu for switching between base tile layers.
 * Displays a layers icon button that reveals a dropdown with layer options on click.
 * @class
 * @extends DropdownControl
 */
export class TileSetSwitcherControl extends DropdownControl {
    /**
     * Creates a new TileSetSwitcherControl instance.
     * @param {Object} [options={}] - Control options
     * @param {Array<Object>} [options.layers=[]] - Array of layer objects with 'name' property
     * @param {Function} [options.onLayerChange] - Callback invoked when layer changes: (layer, index) => void
     * @param {HTMLElement} [options.target] - Target element to render the control into
     */
    constructor(options = {}) {
        super({
            // buttonIconHtml: '<i class="bi bi-signpost-2"></i>',
            buttonIconHtml: '<i class="bi bi-map"></i>',
            buttonClass: 'ol-tileSet-switcher-button',
            buttonTitle: 'Switch map tile set',
            elementClass: 'ol-tileSet-switcher',
            dropdownClass: 'tileSet-switcher-dropdown',
            target: options.target
        });

        this.layers = options.layers || [];
        this.onLayerChange = options.onLayerChange || (() => {});
        this.currentLayerIndex = 0;

        // Populate dropdown with layer options
        this.updateDropdown();
    }

    /**
     * Rebuilds the dropdown menu with current layer options.
     * Highlights the currently active layer.
     * @private
     */
    updateDropdown() {
        this.dropdown.innerHTML = '';

        // Create title bar at the top
        const titleBar = document.createElement('div');
        titleBar.className = 'tileSet-switcher-title';
        titleBar.textContent = 'Select a map tile set:';
        this.dropdown.appendChild(titleBar);

        this.layers.forEach((layer, index) => {
            const item = document.createElement('div');
            item.className = 'tileSet-switcher-item';
            item.textContent = layer.name;

            if (index === this.currentLayerIndex) {
                item.classList.add('active');
            }

            item.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.selectLayer(index);
            });

            this.dropdown.appendChild(item);
        });
    }

    /**
     * Selects a layer by index, updates the UI, and fires the onLayerChange callback.
     * @param {number} index - Index of the layer to select
     */
    selectLayer(index) {
        if (index >= 0 && index < this.layers.length) {
            this.currentLayerIndex = index;
            this.updateDropdown();
            this.hideDropdown();
            this.onLayerChange(this.layers[index], index);
        }
    }

    /**
     * Updates the available layers and refreshes the dropdown.
     * @param {Array<Object>} layers - New array of layer objects with 'name' property
     */
    setLayers(layers) {
        this.layers = layers;
        this.updateDropdown();
    }
}
